import {
  getOrganisationDetails as getOrgDetails,
  getOrganisationUsers as getOrgUsers,
  deactivateUser as deactivateOrgUser,
  inviteUser as inviteOrgUser,
  uploadOrganisationBranding as uploadOrgBranding,
  updateUserRole as updateOrgUserRole,
  bulkInviteUsers as bulkInviteOrgUsers,
  activateUser as activateOrgUser,
  bulkActivateUsers as bulkActivateOrgUsers,
  getReminderSettings as getOrgReminderSettings,
  updateReminderSetting as updateOrgReminderSetting,
  type ActivationResult,
  type BulkActivationInput,
  type BulkActivationOptions,
  type BulkActivationResponse,
  type ReminderSetting,
} from "../api/organizationApi";
import {
  Organisation,
  OrganisationUsersData,
  InviteUserRequest,
} from "../types/types";

export type {
  BulkActivationInput,
  BulkActivationOptions,
  BulkActivationProgress,
  ReminderSetting,
} from "../api/organizationApi";

/**
 * Get organisation details by ID
 * @param organisationId - Organization ID
 * @returns Promise<Organisation>
 */
export async function getOrganisationDetails(
  organisationId: string,
): Promise<Organisation> {
  try {
    const response = await getOrgDetails(organisationId);
    return {
      id: response.id,
      name: response.name,
      total_licences: response.total_licences,
      used_licences: response.used_licences,
      logo_url: response.logo_url ?? null,
      available_licences: response.available_licences,
      admin_count: response.admin_count,
      learner_count: response.learner_count,
      updated_at: response.updated_at,
    };
  } catch (error) {
    console.error("Error fetching organisation details:", error);
    throw error;
  }
}

/**
 * Get users for a specific organisation
 * @param organisationId - Organization ID
 * @param page - Page number
 * @param pageSize - Number of items per page
 * @returns Promise<OrganisationUsersData>
 */
export async function getOrganisationUsers(
  organisationId: string,
  page: number = 1,
  pageSize: number = 10,
): Promise<OrganisationUsersData> {
  try {
    const response = await getOrgUsers(organisationId, page, pageSize);
    return {
      users: response.users.map((user) => ({
        ...user,
        _actions: user._actions ?? undefined,
      })),
      totalCount: response.totalCount,
      currentPage: response.currentPage,
      pageSize: response.pageSize,
      notActivatedCount: response.notActivatedCount,
    };
  } catch (error) {
    console.error("Error fetching organisation users:", error);
    throw error;
  }
}

/**
 * Deactivate a user from an organisation
 * @param organisationId - Organization ID
 * @param userId - User ID to deactivate
 * @returns Promise<void>
 */
export async function deactivateUser(
  organisationId: string,
  userId: string,
): Promise<void> {
  try {
    await deactivateOrgUser(organisationId, userId);
  } catch (error) {
    console.error("Error deactivating user:", error);
    throw error;
  }
}

/**
 * Invite a new user to an organisation
 * @param organisationId - Organization ID
 * @param userData - User data for invitation
 * @returns Promise<void>
 */
export async function inviteUser(
  organisationId: string,
  userData: InviteUserRequest,
): Promise<void> {
  try {
    await inviteOrgUser(organisationId, {
      email: userData.email,
      name: userData.name,
      role: userData.role ?? "learner",
      jobTitle: userData.jobTitle,
      // WS-O P1.1 (Codex PR#546 r2): forward buyer_type so the "decision-maker"
      // invite reaches the backend scoped bypass — without it the org_admin invite
      // was silently dropped and the role hierarchy rejected it.
      buyer_type: userData.buyer_type,
    });

    // TODO: Uncomment code below and add the service role key to the .env file if you want to test admin user creation.
    // const supabase = createClient(
    //   process.env.REACT_APP_SUPABASE_URL!,
    //   process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY!
    // );
    // const { data, error } = await supabase.auth.admin.createUser({
    //   email: userData.email,
    //   user_metadata: { name: userData.name },
    //   email_confirm: false,
    // });

    // if (error) {
    //   throw error;
    // }
  } catch (error) {
    console.error("Error inviting user:", error);
    throw error;
  }
}

/**
 * Upload organization branding logo
 * @param organisationId - Organization ID
 * @param logoFile - Logo file to upload
 * @returns Promise<void>
 */
export async function uploadOrganisationBranding(
  organisationId: string,
  logoFile: File,
): Promise<{ logoUrl: string }> {
  try {
    const response = await uploadOrgBranding(organisationId, logoFile);
    return { logoUrl: response?.logoUrl ?? "" };
  } catch (error) {
    console.error("Error uploading organisation branding:", error);
    throw error;
  }
}

/**
 * Update a user's role within an organisation
 * @param organisationId - Organization ID
 * @param userId - User ID
 * @param role - New role ('learner' | 'admin')
 * @returns Promise with success status and message
 */
export async function updateUserRole(
  organisationId: string,
  userId: string,
  role: "learner" | "admin",
): Promise<{ success: boolean; message: string }> {
  try {
    return await updateOrgUserRole(organisationId, userId, role);
  } catch (error) {
    console.error("Error updating user role:", error);
    throw error;
  }
}

/**
 * Bulk invite users via CSV upload
 * @param organisationId - Organization ID
 * @param file - CSV file containing user data
 * @returns Promise with upload results (total, success, failed counts and per-user results)
 */
export async function bulkInviteUsers(
  organisationId: string,
  file: File,
): Promise<{
  total: number;
  success: number;
  failed: number;
  results: Array<{ email: string; success: boolean; message?: string }>;
}> {
  try {
    return await bulkInviteOrgUsers(organisationId, file);
  } catch (error) {
    console.error("Error bulk inviting users:", error);
    throw error;
  }
}

export async function activateUser(
  organisationId: string,
  userId: string,
): Promise<ActivationResult> {
  return activateOrgUser(organisationId, userId);
}

export async function bulkActivateUsers(
  organisationId: string,
  input: BulkActivationInput,
  options?: BulkActivationOptions,
): Promise<BulkActivationResponse> {
  return bulkActivateOrgUsers(organisationId, input, options);
}

export async function getReminderSettings(
  organisationId: string,
): Promise<ReminderSetting[]> {
  return getOrgReminderSettings(organisationId);
}

export async function updateReminderSetting(
  organisationId: string,
  input: {
    masterCourseId: string;
    overdueFrom: string | null;
    repeatEveryValue?: number;
    repeatEveryUnit?: "days" | "weeks" | "months";
  },
): Promise<ReminderSetting | { cleared: true; masterCourseId: string }> {
  return updateOrgReminderSetting(organisationId, input);
}
